import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import {
  createCalcExport,
  downloadCalcExport,
  getCalcExport,
  preflightCalcExport,
  type CalcExportJob,
  type CalcExportMode,
  type CalcExportPreflight,
  type CalcExportRequest,
} from "../../../api/calc";
import { CALC_EXPORT_COPY } from "../../../api/calcExportCopy";
import { useToast } from "../../common/Toast";
import { useCalcProject } from "../CalcProjectContext";
import { CALC_EXPORT_REGISTRY } from "./CalcExportDocument";

type ExportOpenOptions = { sheetId?: string };
type ExportContextValue = { openExport: (options?: ExportOpenOptions) => void };

const ExportContext = createContext<ExportContextValue | null>(null);

export function useCalcExport() {
  const value = useContext(ExportContext);
  if (!value) throw new Error("useCalcExport must be used inside CalcExportProvider");
  return value;
}

export function CalcExportProvider({ children }: { children: ReactNode }) {
  const [openOptions, setOpenOptions] = useState<ExportOpenOptions | null>(null);
  const value = useMemo(() => ({ openExport: (options: ExportOpenOptions = {}) => setOpenOptions(options) }), []);
  return (
    <ExportContext.Provider value={value}>
      {children}
      {openOptions ? createPortal(<CalcExportWizard options={openOptions} onClose={() => setOpenOptions(null)} />, document.body) : null}
    </ExportContext.Provider>
  );
}

function CalcExportWizard({ options, onClose }: { options: ExportOpenOptions; onClose: () => void }) {
  const { projectId, project, sheets } = useCalcProject();
  const { toast } = useToast();
  const scope = options.sheetId ? "calculation" : "project";
  const availableSheets = options.sheetId ? sheets.filter((sheet) => sheet.id === options.sheetId) : sheets;
  const [mode, setMode] = useState<CalcExportMode>("simplified");
  const [selected, setSelected] = useState(() => new Set(availableSheets.map((sheet) => sheet.id)));
  const [includeCover, setIncludeCover] = useState(scope === "project");
  const [includeToc, setIncludeToc] = useState(scope === "project");
  const [includeDraftLoads, setIncludeDraftLoads] = useState(true);
  const [step, setStep] = useState<"setup" | "review" | "generating">("setup");
  const [preflight, setPreflight] = useState<CalcExportPreflight | null>(null);
  const [job, setJob] = useState<CalcExportJob | null>(null);
  const [busy, setBusy] = useState(false);
  const jobId = job?.id;
  const jobStatus = job?.status;

  const request = useMemo<CalcExportRequest>(() => ({
    scope,
    mode,
    sheetIds: availableSheets.filter((sheet) => selected.has(sheet.id)).map((sheet) => sheet.id),
    includeCover: scope === "project" && includeCover,
    includeToc: scope === "project" && includeToc,
    includeDraftLoads,
  }), [scope, mode, availableSheets, selected, includeCover, includeToc, includeDraftLoads]);

  const selectedSheets = availableSheets.filter((sheet) => selected.has(sheet.id));

  const review = async () => {
    setBusy(true);
    try {
      const value = await preflightCalcExport(projectId, request);
      setPreflight(value);
      setStep("review");
    } catch (error) {
      toast(error instanceof Error ? error.message : "Unable to review this export.", "error");
    } finally {
      setBusy(false);
    }
  };

  const generate = async () => {
    setBusy(true);
    try {
      const created = await createCalcExport(projectId, request);
      setJob(created);
      setStep("generating");
    } catch (error) {
      toast(error instanceof Error ? error.message : "Unable to start this export.", "error");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!jobId || !jobStatus || !["queued", "rendering"].includes(jobStatus)) return;
    let cancelled = false;
    const timer = window.setInterval(() => {
      void getCalcExport(projectId, jobId).then((next) => {
        if (!cancelled) setJob(next);
      }).catch(() => undefined);
    }, 900);
    return () => { cancelled = true; window.clearInterval(timer); };
  }, [jobId, jobStatus, projectId]);

  const download = useCallback(async () => {
    if (!job) return;
    try {
      await downloadCalcExport(job);
      toast(`Downloaded ${job.filename}`, "success");
    } catch (error) {
      toast(error instanceof Error ? error.message : "Unable to download this export.", "error");
    }
  }, [job, toast]);

  const toggleAll = (checked: boolean) => setSelected(checked ? new Set(availableSheets.map((sheet) => sheet.id)) : new Set());

  return (
    <div className="ce-wizard-scrim" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="ce-wizard" role="dialog" aria-modal="true" aria-labelledby="ce-wizard-title">
        <header className="ce-wizard-head">
          <div><span>Calculation export</span><h2 id="ce-wizard-title">{scope === "project" ? project?.name ?? "Project report" : selectedSheets[0]?.name ?? "Calculation"}</h2></div>
          <button type="button" onClick={onClose} aria-label="Close export wizard">×</button>
        </header>
        <div className="ce-wizard-progress" aria-label="Export progress">
          <span className={step === "setup" ? "is-active" : "is-done"}>1 Format</span>
          <span className={step === "review" ? "is-active" : step === "generating" ? "is-done" : ""}>2 Review</span>
          <span className={step === "generating" ? "is-active" : ""}>3 Generate</span>
        </div>

        <div className="ce-wizard-body">
          {step === "setup" ? (
            <>
              <section className="ce-wizard-section"><h3>Report format</h3><p>Choose the level of detail for every selected calculation.</p>
                <div className="ce-mode-grid">
                  <button type="button" aria-pressed={mode === "simplified"} className={mode === "simplified" ? "is-selected" : ""} onClick={() => setMode("simplified")}>
                    <span>Compact summary</span><strong>Simplified</strong><small>Critical inputs, governing results, diagram, primary checks, and warnings, targeted to one sheet.</small>
                  </button>
                  <button type="button" aria-pressed={mode === "detailed"} className={mode === "detailed" ? "is-selected" : ""} onClick={() => setMode("detailed")}>
                    <span>Complete calculation record</span><strong>Detailed</strong><small>All inputs, checks, combinations, factors, intermediates, assumptions, and sources.</small>
                  </button>
                </div>
              </section>

              {scope === "project" ? (
                <section className="ce-wizard-section"><div className="ce-wizard-section-title"><div><h3>Calculations</h3><p>{selected.size} of {availableSheets.length} selected in project order.</p></div>
                  <label><input type="checkbox" checked={selected.size === availableSheets.length && availableSheets.length > 0} onChange={(event) => toggleAll(event.target.checked)} /> Select all</label>
                </div>
                  <div className="ce-sheet-picker">{availableSheets.map((sheet) => (
                    <label key={sheet.id}><input type="checkbox" checked={selected.has(sheet.id)} onChange={(event) => setSelected((current) => { const next = new Set(current); if (event.target.checked) next.add(sheet.id); else next.delete(sheet.id); return next; })} />
                      <span><strong>{sheet.name || "Untitled"}</strong><small>{CALC_EXPORT_REGISTRY[sheet.calculatorId]?.title ?? sheet.calculatorId}</small></span><em>{sheet.status}</em>
                    </label>
                  ))}</div>
                </section>
              ) : null}

              <section className="ce-wizard-section"><h3>Package options</h3>
                {scope === "project" ? <><label className="ce-option" data-helpshot="export-cover-option"><input type="checkbox" checked={includeCover} onChange={(event) => setIncludeCover(event.target.checked)} /><span><strong>Project cover</strong><small>Report identity, responsible engineer, code basis, and set summary.</small></span></label>
                  <label className="ce-option" data-helpshot="export-index-option"><input type="checkbox" checked={includeToc} onChange={(event) => setIncludeToc(event.target.checked)} /><span><strong>Calculation index</strong><small>Marks, groups, statuses, and governing checks in project order.</small></span></label></> : null}
                <label className="ce-option" data-helpshot="export-draft-option"><input type="checkbox" checked={includeDraftLoads} onChange={(event) => setIncludeDraftLoads(event.target.checked)} /><span><strong>{CALC_EXPORT_COPY.allowDraftLoads}</strong><small>No uncalculated load will be presented as an authoritative result.</small></span></label>
              </section>
            </>
          ) : null}

          {step === "review" && preflight ? (
            <>
              <section className="ce-review-summary" data-helpshot="export-review-summary"><div><span>Format</span><strong>{mode === "simplified" ? "Compact summary" : "Detailed report"}</strong></div><div><span>Calculations</span><strong>{preflight.sheets.length}</strong></div><div><span>Preflight</span><strong className={preflight.canExport ? "is-pass" : "is-fail"}>{preflight.canExport ? CALC_EXPORT_COPY.ready : CALC_EXPORT_COPY.needsAttention}</strong></div></section>
              <section className="ce-wizard-section"><h3>Preflight review</h3><p>The report preserves engine statuses and never fills missing results.</p>
                {preflight.issues.length ? <div className="ce-issues" data-helpshot="export-issues">{preflight.issues.map((issue, index) => <div className={`is-${issue.severity}`} key={`${issue.code}-${index}`}><strong>{issue.sheetName || (issue.severity === "error" ? "Required" : "Review")}</strong><span>{issue.message}</span></div>)}</div> : <div className="ce-preflight-clear"><strong>No issues found</strong><span>All selected calculations have exportable results.</span></div>}
              </section>
              {mode === "simplified" ? <div className="ce-wizard-note"><strong>One-page target</strong><span>The layout prioritizes one sheet. Dense calculations continue onto another page rather than clipping or failing.</span></div> : null}
            </>
          ) : null}

          {step === "generating" && job ? (
            <div className={`ce-generation is-${job.status}`}>
              <div className="ce-generation-mark">{job.status === "completed" ? "✓" : job.status === "failed" ? "!" : ""}</div>
              <h3>{job.status === "completed" ? CALC_EXPORT_COPY.reportReady : job.status === "failed" ? CALC_EXPORT_COPY.exportFailed : CALC_EXPORT_COPY.generatingReport}</h3>
              <p>{job.status === "completed" ? `${job.filename} is ready to download.` : job.status === "failed" ? job.error || CALC_EXPORT_COPY.reportFailedFallback : "Laying out calculations and creating the PDF. Dense summaries may continue onto another page."}</p>
              {job.status === "queued" || job.status === "rendering" ? <div className="ce-generation-bar"><span /></div> : null}
            </div>
          ) : null}
        </div>

        <footer className="ce-wizard-foot">
          {step === "setup" ? <><button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button><button type="button" className="btn btn-primary" data-helpshot="export-review" disabled={busy || !selected.size} onClick={() => void review()}>{busy ? "Reviewing..." : CALC_EXPORT_COPY.reviewAction}</button></> : null}
          {step === "review" ? <><button type="button" className="btn btn-secondary" onClick={() => setStep("setup")}>Back</button><button type="button" className="btn btn-primary" data-helpshot="export-generate" disabled={busy || !preflight?.canExport} onClick={() => void generate()}>{busy ? "Starting..." : CALC_EXPORT_COPY.generateAction}</button></> : null}
          {step === "generating" ? <><button type="button" className="btn btn-secondary" onClick={onClose}>{job?.status === "completed" ? "Close" : CALC_EXPORT_COPY.runInBackground}</button>{job?.status === "completed" ? <button type="button" className="btn btn-primary" onClick={() => void download()}>{CALC_EXPORT_COPY.downloadPdf}</button> : job?.status === "failed" ? <button type="button" className="btn btn-primary" onClick={() => setStep("review")}>{CALC_EXPORT_COPY.tryAgain}</button> : null}</> : null}
        </footer>
      </section>
    </div>
  );
}
